import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Box,
  Divider,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Science as ScienceIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { Evidence } from '../types';

interface EvidenceSectionProps {
  evidenceList: Evidence[];
  currentCaseId: string;
  onEvidenceClick: (evidence: Evidence) => void;
}

const EvidenceSection: React.FC<EvidenceSectionProps> = ({
  evidenceList,
  currentCaseId,
  onEvidenceClick
}) => {
  // Filter evidence by case
  const filteredEvidence = evidenceList.filter(evidence => evidence.caseId === currentCaseId);

  return (
    <Box sx={{ mb: 1 }}>
      <Accordion defaultExpanded sx={{ boxShadow: 'none', border: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ 
            minHeight: 40, 
            backgroundColor: '#f8f9fa',
            borderBottom: 'none'
          }}>
            <Box display="flex" alignItems="center">
              <ScienceIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ 
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#2c3e50'
              }}>
                Evidence
              </Typography>
              <Chip
                label={filteredEvidence.length}
                size="small"
                sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                color="primary"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
                     <AccordionDetails sx={{ pt: 1, pb: 1 }}>
            {filteredEvidence.length > 0 ? (
              <List sx={{ p: 0, maxHeight: 250, overflow: 'auto' }}>
                {filteredEvidence.map((evidence, index) => (
                  <React.Fragment key={evidence.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => onEvidenceClick(evidence)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          py: 0.5,
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            '& .MuiTypography-root': {
                              color: 'white'
                            }
                          }
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            mr: 1.5,
                            bgcolor: 'secondary.main',
                            fontSize: '0.75rem'
                          }}
                        >
                          {evidence.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                              {evidence.name}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Box display="flex" alignItems="center" mb={0.25}>
                                <CategoryIcon sx={{ fontSize: 12, mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                         
                                </Typography>
                              </Box>
                                                             <Box display="flex" alignItems="center">
                                 <LocationIcon sx={{ fontSize: 12, mr: 0.5, color: 'text.secondary' }} />
                                 <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                   {evidence.location.global?.name || evidence.location.local?.name}
                                 </Typography>
                               </Box>
                               {evidence.source && (
                                 <Box display="flex" alignItems="center">
                                   <CategoryIcon sx={{ fontSize: 12, mr: 0.5, color: 'text.secondary' }} />
                                   <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                     Source: {evidence.source}
                                   </Typography>
                                 </Box>
                               )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredEvidence.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                py={2}
                color="text.secondary"
              >
                <ScienceIcon sx={{ fontSize: 32, mb: 0.5, opacity: 0.5 }} />
                <Typography variant="caption" textAlign="center" sx={{ fontSize: '0.7rem' }}>
                  No evidence found for this case
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
    </Box>
  );
};

export default EvidenceSection;
